import React, { useEffect, useId, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { api } from "../../../utils/helpers";
import { useLang } from "../i18n";
import { packageStates, vorwaertsAlleZiele, vorwaertsZiele, useTransportV2, withPdEdgeDefaults, pdEdgeMode, pdEdgeRequested, pdEdgeConflictPids } from "../context/TransportV2Context";
import de from "../../../i18n/de/partnerdirectory.json";
import en from "../../../i18n/en/partnerdirectory.json";

export function edgeReason(code, lang) {
  const catalog = lang === "en" ? en : de;
  return catalog.edgeSync.reasons[code] || catalog.edgeSync.reasons.EDGE_REQUEST_FAILED;
}

// Include existing Edge intent in inactive drafts and hidden selected stages.
// Cloud-only partners never need a workflow readiness read.
export function pdEdgeCapabilityStages(state) {
  const drafts = state.selectedPackages?.length ? packageStates(state) : [state];
  return drafts.filter(draft => draft.pdReplicate).flatMap(draft => {
    const stages = vorwaertsAlleZiele(draft);
    return stages.filter(stage => (draft.pdSelectedPids || []).some(pid => pdEdgeRequested(draft, pid)
      && (draft.pdStagesByPid?.[pid] || stages.map(item => item.key)).includes(stage.key)));
  });
}

// Query once per target instance, not once per PID. Ignore responses from an old landscape.
export function usePdEdgeCapabilities(stages, enabled, spaceId) {
  const signature = JSON.stringify(enabled ? [...new Set(stages.map(stage => stage.instanceId).filter(Boolean))] : []);
  const [result, setResult] = useState({ key: "", values: {} });
  const [generation, setGeneration] = useState(0);
  const key = `${spaceId || ""}:${signature}:${generation}`;
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const ids = JSON.parse(signature);
    setResult({ key, values: Object.fromEntries(ids.map(id => [id, { loading: true }])) });
    ids.forEach(async id => {
      try {
        const value = await api(`/api/${encodeURIComponent(id)}/partner-directory/edge-sync/targets`, { signal: controller.signal, cache: "no-store", retry: false, timeout: 30000 });
        if (!value || !Array.isArray(value.targets) || typeof value.available !== "boolean") throw new Error("EDGE_REQUEST_FAILED");
        if (active) setResult(current => ({ ...current, values: { ...current.values, [id]: { ...value, loading: false } } }));
      } catch (error) {
        if (active) setResult(current => ({ ...current, values: { ...current.values, [id]: { loading: false, error: error.code || "EDGE_REQUEST_FAILED" } } }));
      }
    });
    return () => { active = false; controller.abort(); };
  }, [key, signature]);
  return { values: result.key === key ? result.values : {}, reload: () => setGeneration(value => value + 1) };
}

export function edgePlanBlocked(state, capabilities) {
  if (pdEdgeConflictPids(state).length) return true;
  if ((state.selectedPackages || []).length > 1) return packageStates(state).some(draft =>
    edgePlanBlocked({ ...draft, selectedPackages: [] }, capabilities));
  if (!state.pdReplicate) return false;
  for (const pid of state.pdSelectedPids || []) {
    const mode = pdEdgeMode(state, pid);
    if (mode === "mixed") return true;
    if (mode === "edge" && vorwaertsAlleZiele(state).some(stage =>
      (state.pdStagesByPid?.[pid] || vorwaertsAlleZiele(state).map(item => item.key)).includes(stage.key)
      && !state.pdEdgeByPid?.[pid]?.[stage.key])) return true;
  }
  for (const pid of state.pdSelectedPids || []) for (const [stage, runtime] of Object.entries(state.pdEdgeByPid?.[pid] || {}).filter(([key]) => !state.multiTarget || vorwaertsAlleZiele(state).some(item => item.key === key))) {
    const target = state.multiTarget ? vorwaertsAlleZiele(state).find(item => item.key === stage) : (state.space?.stages || []).find(item => item.stageKey === stage);
    const info = capabilities[target?.instanceId];
    if (!info?.available || !info.targets?.some(item => item.id === runtime)) return true;
  }
  return false;
}

/**
 * Nicht gewählte Ziele, die eine Edge Auswahl brauchen (Vertrag V.10): Die Laufzeit wird nie still geraten, die Sperre
 * bleibt. Der Benutzer blendet das Ziel ein und wählt dort. Gemeint sind Ziele mit Modus „edge“ ohne Laufzeit, mit
 * ungültiger Laufzeit oder mit mehreren Laufzeiten ohne ausdrückliche Wahl.
 */
export function edgeHiddenTargets(state, capabilities = {}) {
  if (!state.multiTarget || !state.pdReplicate) return [];
  const visible = new Set(vorwaertsZiele(state).map(column => column.key));
  const hidden = vorwaertsAlleZiele(state).filter(column => !visible.has(column.key));
  const found = new Map();
  for (const pid of state.pdSelectedPids || []) {
    if (!pdEdgeRequested(state, pid)) continue;
    const stages = state.pdStagesByPid?.[pid] || vorwaertsAlleZiele(state).map(column => column.key);
    const mode = pdEdgeMode(state, pid);
    for (const column of hidden.filter(item => stages.includes(item.key))) {
      const runtime = state.pdEdgeByPid?.[pid]?.[column.key];
      const info = capabilities[column.instanceId];
      const invalid = runtime ? !info?.available || !info.targets?.some(item => item.id === runtime)
        : (mode === "edge" || mode === "mixed") || (!Object.prototype.hasOwnProperty.call(state.pdEdgeChoicesByPid?.[pid] || {}, column.key)
          && (info?.error || edgeRuntimeChoiceRequired(info, null, null, column.key)));
      if (invalid) found.set(column.key, column);
    }
  }
  return [...found.values()];
}

/**
 * Hinweis je verdecktem Ziel mit Edge Bedarf und „Ziel einblenden“ (wählt das Ziel, PdPicker zeigt dann die Auswahl).
 * Bei mehreren gewählten Paketen gilt der Hinweis für jedes Paket, nicht nur das aktive (Integration I1): Die Zielauswahl
 * ist gemeinsam, die Partner Directory Auswahl je Paket. Ein Ziel erscheint einmal, mit den betroffenen Paketen.
 */
export function HiddenEdgeTargets({ capabilities, disabled = false, t }) {
  const { state, dispatch } = useTransportV2();
  const drafts = (state.selectedPackages || []).length > 1 ? packageStates(state) : [state];
  const found = new Map();
  for (const draft of drafts) for (const column of edgeHiddenTargets(draft, capabilities)) {
    const entry = found.get(column.key) || { column, packages: [] };
    const name = draft.pkgName || draft.pkgId;
    if (name && !entry.packages.includes(name)) entry.packages.push(name);
    found.set(column.key, entry);
  }
  const items = [...found.values()];
  if (!items.length) return null;
  return <>{items.map(({ column, packages }) => <div key={column.key} role="alert" className="errline">
    <span>{t("pdEdge.hiddenTarget", { name: column.name || column.code })}
      {drafts.length > 1 && packages.length > 0 && <> {t("pdEdge.hiddenTargetPackages", { names: packages.join(", ") })}</>}</span>
    <button type="button" className="btn btn-sec btn-sm" disabled={disabled}
      onClick={() => dispatch({ type: "TOGGLE_STAGE_TARGET", payload: { stage: column.stageKey, targetId: column.targetId } })}>{t("pdEdge.showTarget")}</button>
  </div>)}</>;
}

export function edgeRuntimeChoiceRequired(info, runtimeId, choices, stage) {
  return !runtimeId && !Object.prototype.hasOwnProperty.call(choices || {}, stage)
    && info?.available === true && !info.loading && !info.error
    && info.targets?.length > 1;
}

export function usePdEdgeDefaults(state, dispatch, capabilities, enabled, locked) {
  const drafts = state.selectedPackages?.length ? packageStates(state) : [state];
  const active = enabled && !locked && !state.pdEdgeDefaultsLocked && !state.batch && !state.batchRequest;
  const needsDefaults = active && drafts.some(draft => withPdEdgeDefaults(draft, capabilities) !== draft);
  // Do not create a Cloud-only snapshot while explicitly selected Edge destination is unresolved.
  const pending = active && drafts.some(draft => draft.pdReplicate && !draft.pdEdgeDefaultsLocked
    && (draft.pdSelectedPids || []).some(pid => pdEdgeRequested(draft, pid) && vorwaertsAlleZiele(draft).some(stage => {
      if (!(draft.pdStagesByPid?.[pid] || vorwaertsAlleZiele(draft).map(item => item.key)).includes(stage.key)) return false;
      if (draft.pdEdgeModeByPid?.[pid] === "cloud" || draft.pdEdgeByPid?.[pid]?.[stage.key]
        || Object.prototype.hasOwnProperty.call(draft.pdEdgeChoicesByPid?.[pid] || {}, stage.key)) return false;
      const info = capabilities[stage.instanceId];
      return !info || info.loading || !!info.error || edgeRuntimeChoiceRequired(info, null, null, stage.key);
    })));
  useEffect(() => {
    if (needsDefaults) dispatch({ type: "APPLY_PD_EDGE_DEFAULTS", payload: capabilities,
      scope: { pkgId: state.pkgId, epoch: state.packageEpoch } });
  }, [needsDefaults, capabilities, dispatch, state.pkgId, state.packageEpoch]);
  return pending || needsDefaults;
}

export default function PdEdgeTargets({ pid, stage, runtimeId, capabilities, disabled, onChange, onRetry, choiceRequired = false }) {
  const { t, lang } = useLang();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState("");
  const info = capabilities || { loading: true };
  const targets = info.targets || [];
  const selected = targets.find(target => target.id === runtimeId);
  const reason = info.error || info.reason || (runtimeId && !selected ? "EDGE_TARGET_INVALID" : null);
  const available = !!info.available && !info.loading && targets.length > 0;
  const label = `${pid} · ${stage.name || stage.code} · ${t("pdEdge.destination")}`;
  const chooseEdge = () => {
    if (!available) return;
    if (targets.length === 1) onChange(targets[0].id);
    else { setChosen(selected?.id || ""); setOpen(true); }
  };
  return <Box className="pd-edge-target" sx={{ minWidth: 0 }}>
    <ToggleButtonGroup exclusive size="small" value={runtimeId || choiceRequired ? "edge" : "cloud"} aria-label={label}
      disabled={disabled} onChange={(_, value) => {
        if (value === "cloud" || (value === null && !runtimeId && !choiceRequired)) onChange(null);
        if (value === "edge" || (value === null && choiceRequired)) chooseEdge();
      }}>
      <ToggleButton value="cloud">{t("pdEdge.cloud")}</ToggleButton>
      <ToggleButton value="edge" disabled={disabled || !available}>{t("pdEdge.both")}</ToggleButton>
    </ToggleButtonGroup>
    {choiceRequired && <Typography sx={{ fontSize: 13, mt: .75, color: "var(--t2)" }}>{t("pdEdge.choose")}</Typography>}
    {info.loading && <CircularProgress size={18} aria-label={t("pdEdge.loading")} sx={{ ml: 1 }} />}
    {reason && !info.loading && <Typography component="div" role={info.error || runtimeId ? "alert" : undefined} className="pd-edge-reason" sx={{ fontSize: 13, mt: .75, color: "var(--t2)" }}>
      {edgeReason(reason, lang)} <Button size="small" onClick={onRetry} disabled={disabled}>{t("pdEdge.recheck")}</Button>
    </Typography>}
    {runtimeId && <Box sx={{ mt: .75, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
      <Typography sx={{ fontSize: 13, color: "var(--t2)", overflowWrap: "anywhere" }}>{selected?.name || runtimeId}</Typography>
      {targets.length > 1 && <Button size="small" disabled={disabled || !available} onClick={chooseEdge}>{t("pdEdge.change")}</Button>}
    </Box>}
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" aria-labelledby={`${id}-title`}
      PaperProps={{ className: "iio iio-review-dialog" }}>
      <DialogTitle id={`${id}-title`}>{t("pdEdge.choose")}</DialogTitle>
      <DialogContent><Typography sx={{ mb: 2, overflowWrap: "anywhere" }}>{pid} · {stage.name || stage.code}</Typography>
        <FormControl fullWidth><InputLabel id={`${id}-runtime`}>{t("pdEdge.runtime")}</InputLabel>
          <Select labelId={`${id}-runtime`} label={t("pdEdge.runtime")} value={chosen} onChange={event => setChosen(event.target.value)}
            MenuProps={{ PaperProps: { className: "iio iio-review-menu" } }}>
            {targets.map(target => <MenuItem key={target.id} value={target.id}>{target.name || target.id}</MenuItem>)}
          </Select>
        </FormControl>
        {!available && <Alert severity="warning" sx={{ mt: 2 }}>{edgeReason(reason, lang)}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)}>{t("pdEdge.cancel")}</Button>
        <Tooltip title={!chosen ? t("pdEdge.choose") : ""}><span><Button disabled={disabled || !available || !targets.some(target => target.id === chosen)}
          onClick={() => { onChange(chosen); setOpen(false); }}>{t("pdEdge.apply")}</Button></span></Tooltip>
      </DialogActions>
    </Dialog>
  </Box>;
}
